import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { useUpdateSettings, type CashFlowSettings } from "../api";
import { fmtUsd } from "../lib";

/** Feature settings (cash_flow.admin only). One setting today: the cash
 *  budget threshold behind the red lines + weekly red flags. */
export function AdminTab({ settings }: { settings: CashFlowSettings }) {
  const [value, setValue] = useState(String(settings.cash_threshold));
  const mutation = useUpdateSettings();

  useEffect(() => {
    setValue(String(settings.cash_threshold));
  }, [settings.cash_threshold]);

  const parsed = Number(value);
  const valid = Number.isFinite(parsed) && parsed > 0;
  const dirty = valid && parsed !== settings.cash_threshold;

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6">
      <Card>
        <CardHeader>
          <CardTitle>Cash budget threshold</CardTitle>
          <CardDescription>
            The operating cash budget. Drawn as the dashed red line on the runway extension
            chart, the reference line on the ending-cash chart, and the red flag on
            weekly-detail rows below it.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex items-end gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="cash-threshold">Threshold ($)</Label>
              <Input
                id="cash-threshold"
                type="number"
                min={1}
                step={1000}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className="w-44"
              />
            </div>
            <Button
              disabled={!dirty || mutation.isPending}
              onClick={() => mutation.mutate({ cash_threshold: parsed })}
            >
              {mutation.isPending ? "Saving…" : "Save"}
            </Button>
          </div>
          {!valid && value !== "" && (
            <p className="text-sm text-danger">Enter a positive dollar amount.</p>
          )}
          {mutation.isError && (
            <p className="text-sm text-danger">Save failed: {mutation.error.message}</p>
          )}
          <p className="text-xs text-muted-foreground">
            Current: <b className="text-foreground">{fmtUsd(settings.cash_threshold)}</b>
            {settings.updated_by_name && settings.updated_by_name !== "seed" && (
              <>
                {" "}· last set by {settings.updated_by_name} on{" "}
                {new Date(settings.updated_at).toLocaleDateString()}
              </>
            )}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
